import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Users, ShoppingCart, DollarSign, TrendingUp, Music } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user?.is_admin) {
            toast.error('Admin access required');
            navigate('/');
            return;
        }
        fetchAnalytics();
    }, [user, navigate]);

    const fetchAnalytics = async () => {
        try {
            const response = await axios.get(`${API}/admin/analytics`);
            setAnalytics(response.data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16">
                <div className="text-xl text-muted-foreground">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 px-6 md:px-12 lg:px-24 py-12" data-testid="admin-dashboard">
            <div className="flex items-center justify-between mb-12">
                <div>
                    <h1 className="text-5xl font-bold tracking-tighter mb-2">Admin Dashboard</h1>
                    <p className="text-muted-foreground">Manage your music store</p>
                </div>
                <button 
                    onClick={() => navigate('/admin/products')}
                    className="btn-primary"
                    data-testid="manage-products-button"
                >
                    Manage Products
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                <div className="bg-card border border-border p-6">
                    <div className="flex items-center justify-between mb-4">
                        <Package className="w-8 h-8 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="text-3xl font-bold mb-1">{analytics?.total_products || 0}</div>
                    <div className="text-sm text-muted-foreground">Total Products</div>
                </div>

                <div className="bg-card border border-border p-6">
                    <div className="flex items-center justify-between mb-4">
                        <Users className="w-8 h-8 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="text-3xl font-bold mb-1">{analytics?.total_users || 0}</div>
                    <div className="text-sm text-muted-foreground">Total Users</div>
                </div>

                <div className="bg-card border border-border p-6">
                    <div className="flex items-center justify-between mb-4">
                        <ShoppingCart className="w-8 h-8 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="text-3xl font-bold mb-1">{analytics?.total_purchases || 0}</div>
                    <div className="text-sm text-muted-foreground">Total Orders</div>
                </div>

                <div className="bg-card border border-border p-6">
                    <div className="flex items-center justify-between mb-4">
                        <DollarSign className="w-8 h-8 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="text-3xl font-bold mb-1">${analytics?.total_revenue || 0}</div>
                    <div className="text-sm text-muted-foreground">Total Revenue</div>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 mb-12">
                {/* Products by Type */}
                <div className="bg-card border border-border p-6">
                    <h3 className="text-xl font-semibold mb-6">Products by Type</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Music className="w-5 h-5 text-muted-foreground" />
                                <span>Sample Packs</span>
                            </div>
                            <span className="font-bold">{analytics?.products_by_type?.sample_pack || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Music className="w-5 h-5 text-muted-foreground" />
                                <span>Songs</span>
                            </div>
                            <span className="font-bold">{analytics?.products_by_type?.song || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Music className="w-5 h-5 text-muted-foreground" />
                                <span>Albums</span>
                            </div>
                            <span className="font-bold">{analytics?.products_by_type?.album || 0}</span>
                        </div>
                    </div>
                </div>

                {/* Top Products */}
                <div className="bg-card border border-border p-6">
                    <h3 className="text-xl font-semibold mb-6">Top Selling Products</h3>
                    <div className="space-y-4">
                        {analytics?.top_products?.map((product, index) => (
                            <div key={product.name} className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">
                                        {index + 1}
                                    </div>
                                    <div>
                                        <div className="font-medium text-sm">{product.name}</div>
                                        <div className="text-xs text-muted-foreground">${product.price}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <TrendingUp className="w-4 h-4 text-primary" />
                                    <span className="font-bold">{product.sales_count} sales</span>
                                </div>
                            </div>
                        ))}
                        {(!analytics?.top_products || analytics.top_products.length === 0) && (
                            <p className="text-muted-foreground text-sm">No sales data yet</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Recent Purchases */}
            <div className="bg-card border border-border p-6">
                <h3 className="text-xl font-semibold mb-6">Recent Purchases</h3>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-border">
                                <th className="text-left py-3 text-sm text-muted-foreground">Date</th>
                                <th className="text-left py-3 text-sm text-muted-foreground">Customer</th>
                                <th className="text-left py-3 text-sm text-muted-foreground">Products</th>
                                <th className="text-right py-3 text-sm text-muted-foreground">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {analytics?.recent_purchases?.map((purchase) => (
                                <tr key={purchase.id} className="border-b border-border/50">
                                    <td className="py-4 text-sm">{formatDate(purchase.created_at)}</td>
                                    <td className="py-4 text-sm">{purchase.user?.name || 'Unknown'}</td>
                                    <td className="py-4 text-sm">
                                        {purchase.products?.map(p => p.name).join(', ')}
                                    </td>
                                    <td className="py-4 text-sm text-right font-bold text-primary">
                                        ${purchase.total_amount}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {(!analytics?.recent_purchases || analytics.recent_purchases.length === 0) && (
                        <p className="text-center py-8 text-muted-foreground">No purchases yet</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
